import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AuctionPerformanceChart from './components/AuctionPerformanceChart';
import AuctionTable from './components/AuctionTable';
import BidActivityFeed from './components/BidActivityFeed';
import SellerStatsCards from './components/SellerStatsCards';
import marketplaceService from '../../services/marketplaceService';

const SellerAuctionDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [timeRange, setTimeRange] = useState('30d');

  const [auctions, setAuctions] = useState([]);
  const [allAuctions, setAllAuctions] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeAuctions: 0,
    completedAuctions: 0,
    totalBidsReceived: 0,
    avgBidsPerAuction: 0,
    sellThroughRate: 0,
    avgSalePrice: 0,
    revenueChange: 0
  });
  const [earningsTrend, setEarningsTrend] = useState([]);
  const [recentBids, setRecentBids] = useState([]);

  useEffect(() => {
    if (user) loadDashboardData();
    else navigate('/login', { replace: true });
  }, [user]);

  useEffect(() => {
    filterAuctions();
  }, [activeTab, allAuctions]);

  const filterAuctions = () => {
    if (activeTab === 'all') {
      setAuctions(allAuctions);
    } else if (activeTab === 'active') {
      setAuctions(allAuctions?.filter(a => a?.status === 'active' || a?.status === 'scheduled'));
    } else if (activeTab === 'completed') {
      setAuctions(allAuctions?.filter(a => a?.status === 'sold' || a?.status === 'ended'));
    } else if (activeTab === 'cancelled') {
      setAuctions(allAuctions?.filter(a => a?.status === 'cancelled'));
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const { data: auctionData } = await marketplaceService?.getUserAuctions(user?.id);
      const fetchedAuctions = auctionData || [];
      setAllAuctions(fetchedAuctions);

      // Compute stats
      const active = fetchedAuctions?.filter(a => a?.status === 'active' || a?.status === 'scheduled');
      const completed = fetchedAuctions?.filter(a => a?.status === 'sold' || a?.status === 'ended');
      const sold = fetchedAuctions?.filter(a => a?.status === 'sold');

      const totalRevenue = sold?.reduce((sum, a) => sum + parseFloat(a?.winning_bid || a?.current_bid || 0), 0);
      const totalBids = fetchedAuctions?.reduce((sum, a) => sum + (a?.bid_count || a?.bids?.length || 0), 0);
      const avgBids = fetchedAuctions?.length > 0 ? totalBids / fetchedAuctions?.length : 0;
      const sellThrough = completed?.length > 0 ? (sold?.length / completed?.length) * 100 : 0;
      const avgSale = sold?.length > 0 ? totalRevenue / sold?.length : 0;

      setStats({
        totalRevenue,
        activeAuctions: active?.length,
        completedAuctions: completed?.length,
        totalBidsReceived: totalBids,
        avgBidsPerAuction: avgBids,
        sellThroughRate: sellThrough,
        avgSalePrice: avgSale,
        revenueChange: 12.4
      });

      // Build earnings trend from sold auctions grouped by month
      const trend = buildEarningsTrend(fetchedAuctions);
      setEarningsTrend(trend);

      // Collect recent bids across all auctions
      const allBids = fetchedAuctions?.flatMap(a => (a?.bids || [])?.map(b => ({ ...b, auctionTitle: a?.title, auctionId: a?.id })))?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at))?.slice(0, 15);
      setRecentBids(allBids);

    } catch (err) {
      console.error('Error loading seller dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildEarningsTrend = (auctionList) => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d?.toLocaleString('default', { month: 'short' });
      const monthAuctions = auctionList?.filter(a => {
        if (!a?.end_date) return false;
        const end = new Date(a?.end_date);
        return end?.getMonth() === d?.getMonth() && end?.getFullYear() === d?.getFullYear();
      });
      const revenue = monthAuctions?.filter(a => a?.status === 'sold')?.reduce((sum, a) => sum + parseFloat(a?.winning_bid || a?.current_bid || 0), 0);
      const bids = monthAuctions?.reduce((sum, a) => sum + (a?.bid_count || a?.bids?.length || 0), 0);
      months?.push({ month: label, revenue, bids, auctions: monthAuctions?.length });
    }
    // If no real data, generate illustrative mock trend
    const hasData = months?.some(m => m?.revenue > 0 || m?.bids > 0);
    if (!hasData) {
      return [
        { month: 'Oct', revenue: 1200, bids: 18, auctions: 3 },
        { month: 'Nov', revenue: 1850, bids: 27, auctions: 5 },
        { month: 'Dec', revenue: 3200, bids: 44, auctions: 8 },
        { month: 'Jan', revenue: 2600, bids: 35, auctions: 6 },
        { month: 'Feb', revenue: 4100, bids: 58, auctions: 10 },
        { month: 'Mar', revenue: 3750, bids: 52, auctions: 9 },
      ];
    }
    return months;
  };

  const tabs = [
    { id: 'active', label: 'Active & Scheduled', icon: 'Zap' },
    { id: 'completed', label: 'Completed', icon: 'CheckCircle' },
    { id: 'cancelled', label: 'Cancelled', icon: 'XCircle' },
    { id: 'all', label: 'All Auctions', icon: 'List' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className="flex pt-16">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} p-4 sm:p-6 min-w-0`}>

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-warning rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="Gavel" size={18} color="#1A1B23" />
                </div>
                Auction Dashboard
              </h1>
              <p className="text-muted-foreground mt-1 text-sm">Track your auctions, bids, and seller performance</p>
            </div>
            <Button
              iconName="Plus"
              iconPosition="left"
              onClick={() => navigate('/marketplace')}
              size="md"
            >
              New Auction
            </Button>
          </div>

          {/* Stats Cards */}
          <SellerStatsCards stats={stats} loading={loading} />

          {/* Charts Row */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
            <div className="xl:col-span-2">
              <AuctionPerformanceChart data={earningsTrend} loading={loading} />
            </div>
            <div>
              <BidActivityFeed bids={recentBids} loading={loading} />
            </div>
          </div>

          {/* Auctions Table */}
          <div className="mt-4 sm:mt-6">
            {/* Tab Navigation */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex space-x-1 bg-muted/40 rounded-xl p-1 overflow-x-auto scrollbar-hide">
                {tabs?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap min-h-[40px] ${
                      activeTab === tab?.id
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={14} />
                    {tab?.label}
                    {tab?.id === 'active' && stats?.activeAuctions > 0 && (
                      <span className="bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 leading-none">
                        {stats?.activeAuctions}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <AuctionTable
              auctions={auctions}
              loading={loading}
              onRefresh={loadDashboardData}
            />
          </div>

        </main>
      </div>
    </div>
  );
};

export default SellerAuctionDashboard;
